import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link as RouterLink } from "react-router-dom";

import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TablePagination from "@mui/material/TablePagination";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";

import Spinner from "../../common/Spinner";
import User from "./User";
import { listUser } from "../../../redux/actions/userActions";

const UsersDetails = ({ from, organizationId }) => {
    const dispatch = useDispatch();
    const userList = useSelector((s) => s.userList);
    const { users, totalCount } = userList;
    const userRole = useSelector((s) => s.userLogin?.userInfo?.data?.role);
    const organization = useSelector((s) => s?.organizationDetails?.organization);
    const organizationIds = organizationId ?? organization?.organization_id;
    const orgUserRole = organization?.user_role;

    const [currentPage, setCurrentPage] = useState(0);
    const [role, setRole] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const [userPerPage, setUserPerPage] = useState(20);
    const [loading, setLoading] = useState(false);

    const runList = (overrides = {}) => {
        if (!organizationIds && from !== "admin") return;
        const args = {
            pageNum: currentPage + 1,
            perPage: userPerPage,
            role,
            searchTerm,
            ...overrides,
        };
        setLoading(true);
        dispatch(
            listUser(
                args.pageNum,
                args.perPage,
                "role",
                args.role,
                args.searchTerm,
                userRole,
                from,
                organizationIds,
            ),
        )
            .then(() => setLoading(false))
            .catch(() => setLoading(false));
    };

    useEffect(() => {
        runList({ pageNum: 1 });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userPerPage, organizationIds]);

    const handlePageChange = (_event, newPage) => {
        setCurrentPage(newPage);
        runList({ pageNum: newPage + 1 });
    };

    const handleRowsPerPageChange = (e) => {
        setUserPerPage(Number(e.target.value));
        setCurrentPage(0);
    };

    const handleRoleFilter = (e) => {
        const nextRole = e.target.value;
        setRole(nextRole);
        setCurrentPage(0);
        runList({ role: nextRole, pageNum: 1 });
    };

    const handleSearchKey = (e) => {
        if (e.key === "Enter") {
            setCurrentPage(0);
            runList({ searchTerm, pageNum: 1 });
        }
    };

    const showAddUser = orgUserRole === "Admin" && userRole !== "Super_Admin";

    return (
        <>
            <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, border: "1px solid", borderColor: "divider" }}>
                <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} sx={{ mb: 3 }}>
                    <Typography variant="h3" component="h1">Users</Typography>
                </Stack>

                <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ xs: "stretch", md: "center" }} sx={{ mb: 2 }}>
                    <FormControl size="small" sx={{ minWidth: 180 }}>
                        <InputLabel id="role-filter-label">Filter by role</InputLabel>
                        <Select
                            labelId="role-filter-label"
                            label="Filter by role"
                            value={role}
                            onChange={handleRoleFilter}
                        >
                            <MenuItem value="">All</MenuItem>
                            <MenuItem value="Admin">Admin</MenuItem>
                            <MenuItem value="Member">Member</MenuItem>
                            <MenuItem value="Tester">Tester</MenuItem>
                            <MenuItem value="Client">Client</MenuItem>
                        </Select>
                    </FormControl>

                    <TextField
                        size="small"
                        placeholder="Search users — press Enter"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyDown={handleSearchKey}
                        sx={{ flex: 1, minWidth: 220 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                </InputAdornment>
                            ),
                        }}
                    />

                    <Box sx={{ flex: { md: "0 0 auto" } }}>
                        {showAddUser && (
                            <Button
                                component={RouterLink}
                                to="/user/add"
                                variant="contained"
                                startIcon={<AddIcon />}
                            >
                                Add user
                            </Button>
                        )}
                    </Box>
                </Stack>

                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ "& th": { fontWeight: 700, color: "text.secondary", textTransform: "uppercase", letterSpacing: "0.04em", fontSize: 12, background: "var(--ba-bg-soft, #FAFBFC)" } }}>
                                <TableCell>Name</TableCell>
                                <TableCell>Email</TableCell>
                                <TableCell>Role</TableCell>
                                <TableCell>Status</TableCell>
                                {from !== "organization" && <TableCell align="center">Action</TableCell>}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {users?.length > 0 ? (
                                users.map((u) => (
                                    <User user={u} from={from} organizationId={organizationIds} key={u.id} />
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={from === "organization" ? 4 : 5} align="center" sx={{ color: "text.secondary", py: 6 }}>
                                        No users yet.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                <TablePagination
                    component="div"
                    count={totalCount || users?.length || 0}
                    page={currentPage}
                    onPageChange={handlePageChange}
                    rowsPerPage={userPerPage}
                    onRowsPerPageChange={handleRowsPerPageChange}
                    rowsPerPageOptions={[20, 40, 60, 80, 100]}
                />
            </Paper>

            {loading && <Spinner fullscreen label="Loading users…" />}
        </>
    );
};

export default UsersDetails;
